import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Film, Users, HeartHandshake, Sparkles, Megaphone, Accessibility } from "lucide-react";
import { LandingHighlights } from "../components/LandingHighlights";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5cfbd919-9e3b-4572-9ab4-1404b46a1607/images/16bd2dda24d6e347a57d5cda282a3c0dbb64532bd315420be182b6b57f96cd56.png";
const FILMMAKER_IMG = "https://static.prod-images.emergentagent.com/jobs/5cfbd919-9e3b-4572-9ab4-1404b46a1607/images/a26389d512d3e0c952b7e2311ba1707ce28ea6a661fdf70495b297cf063c6537.png";
const AUDIENCE_IMG = "https://images.unsplash.com/photo-1634361178167-77c8f459103e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxmaWxtJTIwZmVzdGl2YWwlMjBhdWRpZW5jZXxlbnwwfHx8fDE3NzkwMzcyMDd8MA&ixlib=rb-4.1.0&q=85";

const objectives = [
  { icon: Film, title: "Platform the work", text: "Curate, programme and tour films directed, written, edited or scored by neurodivergent artists across the UK." },
  { icon: Users, title: "Build the network", text: "A members directory of vetted neurodivergent professionals — crew, cast, post and music — searchable by role and location." },
  { icon: Accessibility, title: "Set the standard", text: "Publish open, practical accessibility guidance for low-sensory sets, calm call sheets and inclusive recruitment." },
  { icon: Megaphone, title: "Pay the artists", text: "Member fees and partner funding go directly into honoraria, festival travel, and submission fee waivers." },
];

const involve = [
  { kicker: "01 / Watch", title: "Stream this season's selections.", body: "The Films page hosts the current programme — embedded for free, captioned, and never auto-playing.", cta: { to: "/films", label: "Browse the programme" } },
  { kicker: "02 / Join", title: "Become a member.", body: "Pay-what-you-can membership from £10/year unlocks the professional network and supports submission fee waivers for ND artists.", cta: { to: "/membership", label: "Become a member" } },
  { kicker: "03 / Hire", title: "Crew up from the network.", body: "Producers and commissioners — search the directory for editors, DPs, composers, sound and lighting talent.", cta: { to: "/network", label: "Open the directory" } },
  { kicker: "04 / Submit", title: "Submit a film.", body: "Open call runs twice a year. ND artists submit for free. Email us to be added to the next call alert.", cta: { href: "mailto:create@divergentcinema.co.uk?subject=Open%20Call", label: "Email the team", external: true } },
];

export default function Landing() {
  return (
    <div data-testid="page-landing">
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#FFF8E7]/85" />
          <div className="absolute inset-0 halftone-pink opacity-[0.07]" aria-hidden="true" />
        </div>
        {/* Pop-art sticker badge */}
        <div
          aria-hidden="true"
          className="absolute top-24 right-6 md:right-12 lg:right-24 z-10 hidden md:block badge-bob"
        >
          <div className="bg-[#FFD93D] border-2 border-[#0A0A0A] pop-shadow px-5 py-3 -rotate-6">
            <div className="font-heading font-black text-[#0A0A0A] text-base tracking-tight leading-none">NEW SEASON</div>
            <div className="font-heading font-black text-[#FF2E63] text-2xl tracking-tight leading-none mt-1">NOW PLAYING!</div>
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 md:pt-32 md:pb-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-9 rise-in">
            <div className="inline-block bg-[#1A5DFF] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-6 border-2 border-[#0A0A0A]">A neurodivergent film festival</div>
            <h1 className="font-heading font-black text-[#0A0A0A] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight leading-[0.95]">
              Cinema, made the<br />
              <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#0A0A0A]">way our minds work.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
              Divergent Cinema CIC is a non-profit platform for autistic, ADHD, dyslexic, Tourette&apos;s and otherwise
              neurodivergent filmmakers. We programme films, run a vetted professional directory, and write the
              accessibility playbook for everyone else.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-5">
              <Link
                to="/membership"
                data-testid="hero-membership-cta"
                className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-8 py-4 text-base font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF2E63] inline-flex items-center justify-center gap-2"
              >
                Become a member <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/films"
                data-testid="hero-films-cta"
                className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] pop-shadow rounded-none px-8 py-4 text-base font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0A0A0A] inline-flex items-center justify-center gap-2"
              >
                Watch the programme
              </Link>
            </div>
          </div>
          <div className="lg:col-span-3 bg-[#FFF8E7] border-2 border-[#0A0A0A] p-5 text-[#2C2C2C] text-sm leading-relaxed">
            <span className="font-bold text-[#0A0A0A] block mb-2 uppercase tracking-[0.16em]">Currently programming</span>
            8 films from 7 ND artists across 5 cities. Captions, content notes and sensory advisories on every screening.
          </div>
        </div>
      </section>

      {/* OBJECTIVES — Bento Tetris */}
      <section className="py-20 md:py-28 lg:py-32" data-testid="objectives-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 items-end">
            <div className="md:col-span-7">
              <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-4">Our objectives</div>
              <h2 className="font-heading font-bold text-[#0A0A0A] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
                Four jobs. Done slowly,<br />done with care.
              </h2>
            </div>
            <div className="md:col-span-5 text-[#2C2C2C] text-base leading-relaxed">
              We&apos;re a small company with a clear remit. No mission creep — just programming, paying, paying attention, and building infrastructure that didn&apos;t exist for us.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {objectives.map((o, i) => {
              const Icon = o.icon;
              const span = i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : "md:col-span-7";
              const palette = [
                { bg: "bg-[#FFD93D]", icon: "bg-[#FF2E63]", iconColor: "text-[#FFF8E7]" },
                { bg: "bg-[#FF2E63]", icon: "bg-[#FFD93D]", iconColor: "text-[#0A0A0A]", text: "text-[#FFF8E7]", body: "text-[#FFF8E7]" },
                { bg: "bg-[#1A5DFF]", icon: "bg-[#FFD93D]", iconColor: "text-[#0A0A0A]", text: "text-[#FFF8E7]", body: "text-[#FFF8E7]" },
                { bg: "bg-[#FFF8E7]", icon: "bg-[#0A0A0A]", iconColor: "text-[#FF2E63]" },
              ][i % 4];
              return (
                <div
                  key={o.title}
                  data-testid={`objective-${i}`}
                  className={`${span} ${palette.bg} border-2 border-[#0A0A0A] pop-shadow p-8 md:p-12 flex flex-col gap-4`}
                >
                  <div className={`w-14 h-14 ${palette.icon} border-2 border-[#0A0A0A] flex items-center justify-center`}>
                    <Icon className={`w-7 h-7 ${palette.iconColor}`} />
                  </div>
                  <h3 className={`font-heading font-bold text-2xl ${palette.text || "text-[#0A0A0A]"}`}>{o.title}</h3>
                  <p className={`leading-relaxed ${palette.body || "text-[#2C2C2C]"}`}>{o.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* IMAGE + QUOTE */}
      <section className="py-20 md:py-28" data-testid="quote-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6 relative">
            <img src={FILMMAKER_IMG} alt="A vintage film camera resting on a wooden table" className="w-full h-auto object-cover border-2 border-[#0A0A0A]" />
          </div>
          <div className="md:col-span-6">
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-4">A note from the founders</div>
            <blockquote className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#0A0A0A] leading-snug tracking-tight">
              &ldquo;The industry assumes neurodivergence is something to disclose, manage or mask.
              We think it&apos;s a creative asset — and the work speaks for itself.&rdquo;
            </blockquote>
            <div className="mt-6 text-[#2C2C2C]">— The Divergent Cinema curatorial board</div>
          </div>
        </div>
      </section>

      {/* CURRENTLY BACKING + NEXT SCREENING */}
      <LandingHighlights />

      {/* HOW TO GET INVOLVED */}
      <section className="py-20 md:py-28 lg:py-32 bg-[#FFE8A8]" data-testid="involve-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-4">How to get involved</div>
            <h2 className="font-heading font-bold text-[#0A0A0A] text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight max-w-3xl">
              Four doors in. Pick the one that fits your week.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {involve.map((row, i) => (
              <div key={row.title} data-testid={`involve-${i}`} className="bg-white border-2 border-[#0A0A0A] p-8 md:p-10 flex flex-col gap-5">
                <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF2E63]">{row.kicker}</div>
                <h3 className="font-heading font-bold text-2xl text-[#0A0A0A]">{row.title}</h3>
                <p className="text-[#2C2C2C] leading-relaxed flex-1">{row.body}</p>
                {row.cta.external ? (
                  <a
                    href={row.cta.href}
                    target={row.cta.href?.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`involve-cta-${i}`}
                    className="inline-flex items-center gap-2 text-[#0A0A0A] font-medium border-b-2 border-[#FF2E63] pb-1 w-fit hover:text-[#FF2E63] transition-colors"
                  >
                    {row.cta.label} <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <Link
                    to={row.cta.to}
                    data-testid={`involve-cta-${i}`}
                    className="inline-flex items-center gap-2 text-[#0A0A0A] font-medium border-b-2 border-[#FF2E63] pb-1 w-fit hover:text-[#FF2E63] transition-colors"
                  >
                    {row.cta.label} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIP CTA */}
      <section className="py-20 md:py-28 lg:py-32 relative" data-testid="membership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-[#0A0A0A] text-[#FFF8E7] border-2 border-[#0A0A0A] p-10 md:p-20 grid grid-cols-1 md:grid-cols-12 gap-12 items-center overflow-hidden">
            <div className="absolute -top-6 -right-6 w-48 h-48 halftone-pink opacity-25 pointer-events-none" aria-hidden="true" />
            <div className="md:col-span-7 relative">
              <div className="inline-block bg-[#FFD93D] text-[#0A0A0A] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#FFD93D]">Membership</div>
              <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1]">
                Pay-what-you-can.<br />
                <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#FFD93D]">Unlock the network.</span>
              </h2>
              <p className="mt-6 text-[#FFE8A8] leading-relaxed text-lg max-w-xl">
                Membership funds artist honoraria and submission fee waivers, and gives you access to our directory of vetted neurodivergent professionals — editors, producers, actors, sound and lighting crew.
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end relative">
              <Link
                to="/membership"
                data-testid="membership-cta"
                className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#FFD93D] pop-shadow-yellow rounded-none px-10 py-5 text-base font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFD93D] inline-flex items-center gap-2"
              >
                Join Divergent Cinema <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNITY IMAGE BAND */}
      <section className="relative h-[400px] md:h-[520px] overflow-hidden" data-testid="community-band">
        <img src={AUDIENCE_IMG} alt="Audience watching a film together in a cinema" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0A]/55" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-16">
          <div className="max-w-2xl">
            <div className="text-sm uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-3">Watch together</div>
            <h2 className="font-heading font-bold text-[#FFF8E7] text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              Curated programmes touring independent cinemas across the UK.
            </h2>
          </div>
        </div>
      </section>
    </div>
  );
}
